/****************************************************
 * Module Name:  fancyColorMaster.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { Component } from "react";
import { DataGridPro, GridToolbarExport, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import { Box, Button, Tooltip } from "@mui/material";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import renderCellExpand from "../common/dataGridCellExpand";
import Constants from "../../utility/constants";
import Utility from "../../utility/utility";
import CommonSnackbar from "../common/commonSnackBar";
import NoPermission from "../common/noPermission";
import FancyColorProxy from "../../utility/proxy/manager/fancyColorProxy";
import AddFancyColorModal from "./addFancyColorModal";
import PageTitle from "../common/pageTitle";

export class FancyColorMaster extends Component {
    constructor(props) {
        super(props);

        this.state = {
            fancycolors: [],
            showAddFancyColorModal: false,
            loadingButton: false,
            editOperation: false,
            sourceData: {},
            errorSnackbar: false,
            accesDenied: false,
            showFancyColorLoadSpinner: true,
            local_Permission: Constants.USER_PERMISSION.filter(
                (iPermission) =>
                    iPermission.permissionid === Constants.PERMISSIONS.MASTER_FANCY_COLOR
            ),
        };
        this.checkPermission()
    }

    componentDidMount() {
        PageTitle("MASTER_FANCY_COLOR");
    }

    checkPermission = () => {
        let allowed = Utility.isReadAllowed(this.state.local_Permission)
        if (!allowed) {
            this.setState({
                accesDenied: true
            })
        } else {
            this.fetchFancyColorData(false);
        }
    }

    fetchFancyColorData = (bAnimateSpinner) => {
        bAnimateSpinner && this.setState({ showFancyColorLoadSpinner: true });
        FancyColorProxy.GetAllFancyColor((responseData) => {
            if (responseData) {
                this.setState({ fancycolors: responseData, showFancyColorLoadSpinner: false })
            } else {
                this.setState({ errorSnackbar: true, showFancyColorLoadSpinner: false })
            }
        }
        );
    };

    columns = [
        {
            field: "id",
            headerName: "ID",
            headerAlign: "center",
            align: "center",
        },
        {
            field: "fancycolordescription",
            headerName: "COL DESCRIP",
            headerAlign: "center",
            align: "center",
            flex: 1,
            renderCell: renderCellExpand,
        },
        {
            field: "shortname",
            headerName: "SHORT NAME",
            headerAlign: "center",
            align: "center",
            flex: 1,
            renderCell: renderCellExpand,
        },
        {
            field: "fancycolor1",
            headerName: "F.COL 1",
            headerAlign: "center",
            align: "center",
            flex: 1,
            renderCell: renderCellExpand,
        },
        {
            field: "fancycolor2",
            headerName: "F.COL 2",
            headerAlign: "center",
            align: "center",
            flex: 1,
            renderCell: renderCellExpand,
        },
        {
            field: "fancycolorintensity",
            headerName: "F.COL.INTENSITY",
            headerAlign: "center",
            align: "center",
            width: 140,
            renderCell: renderCellExpand,
        },
        {
            field: "fancycolorovertone",
            headerName: "OVERTONE",
            headerAlign: "center",
            align: "center",
            flex: 1,
            renderCell: renderCellExpand,
        },
        {
            field: "edit",
            headerName: "EDIT",
            headerAlign: "center",
            align: "center",
            flex: 0.5,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                return (
                    <Box
                        style={{ textAlign: "center", cursor: "pointer" }}
                        onClick={() => {
                            this.setState({
                                showAddFancyColorModal: true,
                                editOperation: true,
                                sourceData: params.row,
                            });
                        }}
                    >
                        {" "}
                        <BorderColorIcon color="primary" />
                    </Box>
                );
            },
        },
    ];

    hideAddFancyColorModal = () => {
        this.setState({
            showAddFancyColorModal: false,
        });
    };

    handleValidation = (colorData) => {
        //TODO do validation if needed here
        return true;
    };

    addFancyColor = (colorData) => {
        this.setState({ loadingButton: true });
        if (this.state.editOperation) {
            //Updating Color, and once color is updated on call refresh the list of current colors
            FancyColorProxy.UpdateFancyColor(colorData, (response) => {
                if (response) {
                    this.fetchFancyColorData(true);
                    this.setState({ loadingButton: false });
                    //Hidding color modal
                    this.hideAddFancyColorModal();
                } else {
                    this.setState({ errorSnackbar: true });
                    this.setState({ loadingButton: false });
                }
            });
        } else {
            //Creating new Color, and once color is created on call refresh the list of current colors
            FancyColorProxy.AddFancyColor(colorData, (response) => {
                if (response) {
                    this.fetchFancyColorData(true);
                    this.setState({ loadingButton: false });
                    //Hidding color modal
                    this.hideAddFancyColorModal();
                } else {
                    this.setState({ errorSnackbar: true });
                    this.setState({ loadingButton: false });
                }
            });
        }
    };

    render() {
        const CustomToolbar = () => {
            return (
                <GridToolbarContainer
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        marginHorizontal: "5px",
                        "&.MuiDataGrid-toolbarContainer": {
                            padding: "0px 4px !important",
                        },
                    }}
                >
                    <GridToolbarColumnsButton />
                    <GridToolbarFilterButton />
                    <GridToolbarExport />
                    <Box sx={{ flexGrow: 1, display: { sm: "none", md: "block" } }} />
                    {this.state.showFancyColorLoadSpinner ? (
                        <CircularProgress size="1rem" />
                    ) : (
                        <Tooltip title="Reload List">
                            <RefreshOutlinedIcon
                                color="primary"
                                fontSize="medium"
                                onClick={() => this.fetchFancyColorData(true)}
                                sx={{ cursor: "pointer" }}
                            />
                        </Tooltip>
                    )}

                    {Utility.isAddAllowed(this.state.local_Permission) ? (
                        <Tooltip title="Add Fancy Color">
                            <Button
                                color="info"
                                variant="outlined"
                                size="small"
                                sx={{ ml: "5px" }}
                                onClick={() =>
                                    this.setState({
                                        showAddFancyColorModal: true,
                                        editOperation: false,
                                        sourceData: {},
                                    })
                                }
                                startIcon={<AddBoxOutlinedIcon size="large" />}
                            >
                                Fancy Color
                            </Button>
                        </Tooltip>
                    ) : null}
                    <GridToolbarQuickFilter debounceMs={500} />
                </GridToolbarContainer>
            );
        }
        let allowed = Utility.isReadAllowed(this.state.local_Permission)
        if (!allowed) {
            return <NoPermission />
        }
        const rows = this.state.fancycolors;
        const columns = Utility.isUpdateAllowed(this.state.local_Permission)
            ? this.columns
            : this.columns.filter((col) => col.field !== "edit");
        return (
            <Box>
                <CommonSnackbar
                    show={this.state.errorSnackbar}
                    message={<Box>{Constants.APiFailErrorMessage}</Box>}
                    variant={"error"}
                    autoHide={Constants.SnackBarCloseTimer}
                    handleClose={() => this.setState({ errorSnackbar: false })}
                    title="Failed"
                />
                <br />
                {this.state.showAddFancyColorModal ? (
                    <AddFancyColorModal
                        Show={true}
                        handleClose={this.hideAddFancyColorModal}
                        handleSave={this.addFancyColor}
                        handleValidation={this.handleValidation}
                        editOperation={this.state.editOperation}
                        sourceData={this.state.sourceData}
                        loading={this.state.loadingButton}
                    />
                ) : (
                    <Box />
                )}
                <Box px={1}>
                    <Box
                        sx={{
                            backgroundColor: '#fff',
                            color: '#000',
                            width: "100%",
                            position: "relative",
                            "& .MuiTablePagination-selectLabel": {
                                marginTop: "15px",
                            },
                            "& .MuiTablePagination-displayedRows": {
                                marginTop: "15px",
                            },
                            "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            "& .css-cc8tf1": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            height: "85vh",
                        }}
                    >
                        <DataGridPro
                            disableRowSelectionOnClick={true}
                            loading={this.state.showFancyColorLoadSpinner}
                            sx={{
                                '& .MuiDataGrid-columnHeader': {
                                    color: '#fff',
                                    backgroundColor: 'grey',
                                },
                                "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                    py: "4px",
                                },
                                "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                    py: "12px",
                                },
                                "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                    py: "18px",
                                }
                            }}
                            density="compact"
                            slots={{ toolbar: CustomToolbar }}
                            rows={rows}
                            columns={columns}
                            getRowId={(row) => row.id}
                            initialState={{
                                columns: {
                                    columnVisibilityModel: {
                                        // Hide columns status and traderName, the other columns will remain visible
                                        id: false,
                                    },
                                },
                                pagination: {
                                    paginationModel: Constants.pageSize,
                                },
                            }}
                            pageSizeOptions={Constants.pageSizeOptions}
                            pagination
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: { debounceMs: 500 },
                                },
                            }}
                        />
                    </Box>
                </Box>
            </Box>
        );
    }
}

export default FancyColorMaster;
